import type { GiftCard } from '@nucleus-commerce/api-client/admin';

const DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isDateInput(value: string): boolean {
  return DATE.test(value);
}

/**
 * Expiry is picked as a day and read in UTC (the admin shows UTC today): the card stays usable
 * through the end of that day.
 */
export function fromExpiryInput(value: string): string | null {
  return value === '' ? null : `${value}T23:59:59.999Z`;
}

export function toExpiryInput(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

/** A day in the past (or today, already ending) cannot be an expiry. */
export function isFutureDay(value: string, now: Date = new Date()): boolean {
  return new Date(`${value}T23:59:59.999Z`) > now;
}

/** Only a disabled card can be re-enabled; expired and depleted are derived by the backend. */
export function statusAction(card: Pick<GiftCard, 'status'>): 'disable' | 'enable' {
  return card.status === 'disabled' ? 'enable' : 'disable';
}

export const LAST4 = /^[A-Za-z0-9]{4}$/;

/**
 * The recipient typed at issue lives on the note as a `Recipient: <email>` suffix; the backend
 * reads it the same way, and a suffix cut by the note's length limit reads as none.
 */
export function recipientFromNote(note: string | null): string | null {
  const match = note?.match(/Recipient: (\S+@\S+)$/);
  return match?.[1]?.toLowerCase() ?? null;
}

/** Not an email, so it never collides with an address. */
export const CUSTOMER_DEFAULT = 'customer';

export interface SendTarget {
  kind: 'customer' | 'recipient';
  /** The address sent as `to`, or CUSTOMER_DEFAULT to leave it out so the backend picks the customer. */
  value: string;
  email: string | null;
}

/**
 * Where the code may be emailed: the attached customer and the recipient noted at issue, the only
 * addresses the backend accepts. `customerEmail` is unknown without customers.read.
 */
export function sendTargets(
  card: Pick<GiftCard, 'customerId' | 'note'>,
  customerEmail: string | null,
): SendTarget[] {
  const targets: SendTarget[] = [];
  if (card.customerId) {
    targets.push({
      kind: 'customer',
      value: customerEmail ?? CUSTOMER_DEFAULT,
      email: customerEmail,
    });
  }
  const recipient = recipientFromNote(card.note);
  if (recipient && recipient !== customerEmail?.toLowerCase()) {
    targets.push({ kind: 'recipient', value: recipient, email: recipient });
  }
  return targets;
}

export type SendBlocker = 'codeNotKept' | 'inactive' | 'noRecipient';

/** Why the code cannot be emailed right now, mirroring the backend's 409 and 422 refusals. */
export function sendBlocker(
  card: Pick<GiftCard, 'canSendEmail' | 'status' | 'customerId' | 'note'>,
): SendBlocker | null {
  if (!card.canSendEmail) return 'codeNotKept';
  if (card.status !== 'active') return 'inactive';
  if (!card.customerId && !recipientFromNote(card.note)) return 'noRecipient';
  return null;
}
